/**
 * The shared WebAudio graph: one AudioContext for combat, UI, ambience and music.
 *
 *   sfx (own) ─────────────┐
 *   sfx (others, -3 dB) ─duck─┤→ sfx bus ─┬────────────────┐
 *                               └→ space (one shared short reverb per scene) ─┤
 *   ui ───────────────────────────────────────────────────┤
 *   ambience ─duck────────────────────────────────────────┤→ master → glue → limiter → out
 *   music ───────────────────────────────────────────────┘
 *
 * - Hierarchy: your own SFX sit 3 dB over everyone else's (party, mobs); the clips themselves are
 *   loudness-normalised per role offline (scripts/audio/build.mjs), so bus gains stay at unity.
 * - Space: no reverb is baked into the clips; `setSpace()` picks one shared space for the scene
 *   (outdoor: a short, dark, quiet tail; dungeon: a longer stone room), so every sound sits in
 *   the same place.
 * - Bus volumes come from audio/settings.ts (perceptual curve v^1.5), applied live.
 * - `duck()` dips other units' SFX and the ambience for a moment, so your own big moments and
 *   critical warnings (boss telegraph, your death, low health) cut through a busy pull.
 * - Browser autoplay: the context is created / resumed on the first user gesture only (or when
 *   the browser reports sticky user activation). It is suspended while the tab is hidden.
 * - Nothing here throws: without WebAudio every call is a no-op.
 */
import { combatAudioSettings, type CombatAudioSettings } from './settings';

export type BusId = 'sfx' | 'ui' | 'amb' | 'music';

/** Overall trim into the master chain. */
const MASTER_TRIM = 1.25;
/** Other units' SFX (party, mobs) relative to your own: -3 dB. */
export const OTHERS_GAIN = 0.71;

export type SpaceKind = 'none' | 'hub' | 'outdoor' | 'dungeon';
/** Per-scene space: impulse length (s), decay time constant (s), wet send, IR low-pass (0..1, higher = darker). */
export const SPACES: Readonly<Record<SpaceKind, { len: number; tau: number; wet: number; dark: number }>> = {
  none: { len: 0.1, tau: 0.02, wet: 0, dark: 0.5 },
  hub: { len: 0.9, tau: 0.22, wet: 0.1, dark: 0.6 },
  outdoor: { len: 0.6, tau: 0.12, wet: 0.08, dark: 0.75 },
  dungeon: { len: 1.6, tau: 0.42, wet: 0.17, dark: 0.55 },
};

type AC = typeof AudioContext;

/** Perceptual volume curve. */
export function busLevel(v: number): number {
  return Math.pow(Math.max(0, Math.min(1, v)), 1.5);
}

interface Graph {
  master: GainNode;
  /** Shared space send (null without ConvolverNode support). */
  spaceSend: GainNode | null;
  space: ConvolverNode | null;
  sfx: GainNode;
  sfxOwn: GainNode;
  sfxOther: GainNode;
  ui: GainNode;
  amb: GainNode;
  ambDuck: GainNode;
  music: GainNode;
}

export class Mixer {
  ctx: AudioContext | null = null;
  private g: Graph | null = null;
  private gestured = false;
  private installed = false;
  private settings: Readonly<CombatAudioSettings> = combatAudioSettings.get();
  private readonly readyFns: ((ctx: AudioContext) => void)[] = [];
  private duckUntil = 0;
  private spaceKind: SpaceKind = 'none';

  /** Listen for the first gesture, tab visibility and settings changes (idempotent). */
  install(): void {
    if (this.installed) return;
    this.installed = true;
    combatAudioSettings.subscribe((s) => {
      this.settings = s;
      if (s.enabled && this.gestured && !this.ctx) this.ensure();
      this.applyLevels();
    });
    if (typeof window === 'undefined') return;
    const gesture = (): void => this.gesture();
    // The game's input layer may stop key / pointer events before these run, so poll() also
    // checks the browser's sticky user activation.
    for (const type of ['pointerdown', 'pointerup', 'keydown', 'keyup', 'click', 'touchend'] as const) window.addEventListener(type, gesture, { capture: true, passive: true });
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        const ctx = this.ctx;
        if (!ctx) return;
        try {
          if (document.hidden) void ctx.suspend().catch(() => undefined);
          else if (this.settings.enabled) void ctx.resume().catch(() => undefined);
        } catch {
          /* closed context */
        }
      });
    }
  }

  /** A user gesture happened: create / resume the context. */
  gesture(): void {
    this.gestured = true;
    if (!this.settings.enabled) return;
    const ctx = this.ensure();
    if (ctx && ctx.state === 'suspended' && !(typeof document !== 'undefined' && document.hidden)) void ctx.resume().catch(() => undefined);
  }

  /** Per-frame: pick up sticky user activation when the game swallowed the gesture events. */
  poll(): void {
    if (this.gestured) return;
    const ua = (globalThis.navigator as (Navigator & { userActivation?: { hasBeenActive: boolean } }) | undefined)?.userActivation;
    if (ua?.hasBeenActive) this.gesture();
  }

  get running(): boolean {
    return !!this.ctx && this.ctx.state === 'running';
  }

  get enabled(): boolean {
    return this.settings.enabled;
  }

  /** Current linear level of a bus (0 when muted), for callers that skip work when silent. */
  level(bus: BusId): number {
    const s = this.settings;
    if (!s.enabled || s.master <= 0.001) return 0;
    return busLevel(bus === 'sfx' ? s.volume : bus === 'ui' ? s.ui : bus === 'amb' ? s.ambience : s.music);
  }

  /** Call `fn` once the context exists (now if it already does). */
  onReady(fn: (ctx: AudioContext) => void): void {
    if (this.ctx) fn(this.ctx);
    else this.readyFns.push(fn);
  }

  /** Bus input node, or null without a context. `own`: the local player's SFX (never ducked). */
  input(bus: BusId, own = false): AudioNode | null {
    const g = this.g;
    if (!g) return null;
    if (bus === 'sfx') return own ? g.sfxOwn : g.sfxOther;
    if (bus === 'ui') return g.ui;
    if (bus === 'amb') return g.ambDuck;
    return g.music;
  }

  /** Dip other units' SFX and the ambience to `depth` for `holdS`, then recover. */
  duck(depth = 0.45, holdS = 0.4): void {
    const ctx = this.ctx;
    const g = this.g;
    if (!ctx || !g) return;
    const now = ctx.currentTime;
    const until = now + holdS;
    if (until <= this.duckUntil - 0.05) return;
    this.duckUntil = until;
    for (const [n, base] of [
      [g.sfxOther.gain, OTHERS_GAIN],
      [g.ambDuck.gain, 1],
    ] as const) {
      n.cancelScheduledValues(now);
      n.setTargetAtTime(depth * base, now, 0.03);
      n.setTargetAtTime(base, until, 0.3);
    }
  }

  /** The scene's shared space (outdoor / dungeon / hub). Safe before the context exists. */
  setSpace(kind: SpaceKind): void {
    if (kind === this.spaceKind && this.g?.space?.buffer) return;
    this.spaceKind = kind;
    this.applySpace();
  }

  get space(): SpaceKind {
    return this.spaceKind;
  }

  private applySpace(): void {
    const ctx = this.ctx;
    const g = this.g;
    if (!ctx || !g?.space || !g.spaceSend) return;
    const sp = SPACES[this.spaceKind];
    try {
      const t = ctx.currentTime;
      g.spaceSend.gain.setTargetAtTime(sp.wet, t, 0.05);
      if (sp.wet > 0) g.space.buffer = makeImpulse(ctx, sp.len, sp.tau, sp.dark);
    } catch {
      /* no space */
    }
  }

  private applyLevels(): void {
    const ctx = this.ctx;
    const g = this.g;
    if (!ctx || !g) return;
    const s = this.settings;
    const t = ctx.currentTime;
    g.master.gain.setTargetAtTime(s.enabled ? busLevel(s.master) * MASTER_TRIM : 0, t, 0.03);
    g.sfx.gain.setTargetAtTime(busLevel(s.volume), t, 0.03);
    g.ui.gain.setTargetAtTime(busLevel(s.ui), t, 0.03);
    g.amb.gain.setTargetAtTime(busLevel(s.ambience), t, 0.05);
    g.music.gain.setTargetAtTime(busLevel(s.music), t, 0.03);
    try {
      // Sound off: suspend the whole graph (no CPU for silent loops); back on: resume.
      if (!s.enabled && ctx.state === 'running') void ctx.suspend().catch(() => undefined);
      else if (s.enabled && this.gestured && ctx.state === 'suspended' && !(typeof document !== 'undefined' && document.hidden)) void ctx.resume().catch(() => undefined);
    } catch {
      /* closed context */
    }
  }

  private ensure(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (!this.gestured) return null;
    const gl = globalThis as { AudioContext?: AC; webkitAudioContext?: AC };
    const Ctor = gl.AudioContext ?? gl.webkitAudioContext;
    if (!Ctor) return null;
    try {
      const ctx = new Ctor({ latencyHint: 'interactive' });
      const gain = (to: AudioNode, v = 1): GainNode => {
        const n = ctx.createGain();
        n.gain.value = v;
        n.connect(to);
        return n;
      };
      // Glue: gentle bus compression so a pull sits together.
      const glue = ctx.createDynamicsCompressor();
      glue.threshold.value = -18;
      glue.knee.value = 12;
      glue.ratio.value = 3;
      glue.attack.value = 0.005;
      glue.release.value = 0.2;
      // Brickwall-ish limiter: nothing clips, however big the pull.
      const lim = ctx.createDynamicsCompressor();
      lim.threshold.value = -2;
      lim.knee.value = 0;
      lim.ratio.value = 20;
      lim.attack.value = 0.001;
      lim.release.value = 0.08;
      const out = ctx.createGain();
      out.gain.value = 0.95;
      glue.connect(lim).connect(out).connect(ctx.destination);
      const master = gain(glue, 0);
      const sfx = gain(master);
      const amb = gain(master);
      let space: ConvolverNode | null = null;
      let spaceSend: GainNode | null = null;
      if (typeof ctx.createConvolver === 'function') {
        space = ctx.createConvolver();
        space.normalize = true;
        space.connect(master);
        spaceSend = gain(space, 0);
        sfx.connect(spaceSend);
      }
      this.g = {
        master,
        spaceSend,
        space,
        sfx,
        sfxOwn: gain(sfx),
        sfxOther: gain(sfx, OTHERS_GAIN),
        ui: gain(master),
        amb,
        ambDuck: gain(amb),
        music: gain(master),
      };
      this.ctx = ctx;
      this.applyLevels();
      this.applySpace();
      for (const fn of this.readyFns.splice(0)) {
        try {
          fn(ctx);
        } catch {
          /* a listener failing must not break audio */
        }
      }
    } catch {
      this.ctx = null;
      this.g = null;
    }
    return this.ctx;
  }
}

/**
 * A synthetic stereo impulse response: decaying noise (time constant `tau`), darkened by a one-pole
 * low-pass whose coefficient `dark` (0..1) grows along the tail, decorrelated per channel.
 */
export function makeImpulse(ctx: BaseAudioContext, len: number, tau: number, dark: number): AudioBuffer {
  const n = Math.max(1, Math.round(len * ctx.sampleRate));
  const buf = ctx.createBuffer(2, n, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let lp = 0;
    // Short pre-delay (early reflections arrive after ~8 ms).
    const pre = Math.round(0.008 * ctx.sampleRate);
    for (let i = pre; i < n; i++) {
      const t = (i - pre) / ctx.sampleRate;
      const k = Math.min(0.97, dark * (0.6 + 0.4 * (i / n)));
      lp = lp * k + (Math.random() * 2 - 1) * (1 - k);
      d[i] = lp * Math.exp(-t / tau);
    }
  }
  return buf;
}

/** The shared mixer. */
export const mixer = new Mixer();
